import React, { useState, useEffect, useCallback } from 'react';
import api from 'lib/api';
import Icon from 'components/AppIcon';
import { useBranch } from 'contexts/BranchContext';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'online', label: 'Online' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

const CATEGORIES = [
  { value: 'supplement', label: 'Supplement' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'food', label: 'Food' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS = {
  supplement: 'var(--color-primary)',
  merchandise: 'var(--color-accent)',
  food: '#22c55e',
  other: 'var(--color-muted-foreground)',
};

// ─── Sub-tab: POS Terminal ────────────────────────────────────────────────────

function POSTerminal({ branchId }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [completing, setCompleting] = useState(false);
  const [successSale, setSuccessSale] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');

  const fetchProducts = useCallback(async () => {
    if (!branchId) return;
    setLoadingProducts(true);
    try {
      const { data } = await api.get('/products', {
        params: { branch_id: branchId, search: search || undefined, category: filterCategory || undefined },
      });
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [branchId, search, filterCategory]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Clear cart when branch changes
  useEffect(() => { setCart([]); }, [branchId]);

  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/members', { params: { search: memberSearch, member_status: 'active' } });
        setMemberResults((data || []).slice(0, 8));
      } catch (err) { console.error(err); }
    }, 300);
    return () => clearTimeout(t);
  }, [memberSearch]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        if (existing.qty >= Number(product.stock_qty)) return prev;
        return prev.map(i => i.product_id === product.id
          ? { ...i, qty: i.qty + 1, total: (i.qty + 1) * i.unit_price }
          : i
        );
      }
      if (Number(product.stock_qty) === 0) return prev;
      return [...prev, {
        product_id: product.id,
        name: product.name,
        qty: 1,
        unit_price: Number(product.price),
        total: Number(product.price),
        stock_qty: Number(product.stock_qty),
      }];
    });
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i.product_id !== productId));
      return;
    }
    setCart(prev => prev.map(i => i.product_id === productId
      ? { ...i, qty, total: qty * i.unit_price }
      : i
    ));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(i => i.product_id !== productId));
  };

  const subtotal = cart.reduce((s, i) => s + i.total, 0);
  const discountAmt = parseFloat(discount) || 0;
  const taxAmt = parseFloat(tax) || 0;
  const total = Math.max(0, subtotal - discountAmt + taxAmt);

  const completeSale = async () => {
    if (!branchId) return;
    if (cart.length === 0) return;
    setCompleting(true);
    try {
      const { data } = await api.post('/pos-sales', {
        branch_id: branchId,
        member_id: selectedMember?.id || undefined,
        items: cart.map(i => ({ product_id: i.product_id, qty: i.qty })),
        discount: discountAmt,
        tax: taxAmt,
        payment_method: paymentMethod,
        notes: notes || undefined,
      });
      setSuccessSale(data);
      setCart([]);
      setDiscount('');
      setTax('');
      setNotes('');
      setSelectedMember(null);
      setMemberSearch('');
      fetchProducts();
    } catch (err) {
      console.error('Sale error:', err);
      alert(err?.response?.data?.message || 'Failed to complete sale');
    } finally {
      setCompleting(false);
    }
  };

  if (successSale) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center">
          <Icon name="CheckCircle" size={32} color="#22c55e" />
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Sale Complete!</p>
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            {successSale.sale_number} · ₹{Number(successSale.total).toFixed(2)}
          </p>
        </div>
        <button
          onClick={() => setSuccessSale(null)}
          className="px-4 py-2 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] transition-colors"
        >
          New Sale
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col xl:flex-row gap-4">
      {/* Left: Product grid */}
      <div className="flex-1 min-w-0">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex-1 min-w-[160px] relative">
            <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {!branchId ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
              <Icon name="MapPin" size={22} color="var(--color-primary)" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">No branch selected</p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Select a branch from the top navigation to continue</p>
            </div>
          </div>
        ) : loadingProducts ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-[var(--color-muted-foreground)]">
            <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
            <p className="text-sm font-[var(--font-caption)]">No products found. Add some from the Products tab.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.map(product => {
              const inCart = cart.find(i => i.product_id === product.id);
              const outOfStock = Number(product.stock_qty) === 0;
              return (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  disabled={outOfStock}
                  className={`relative text-left p-3 rounded-[var(--radius-md)] border transition-all ${
                    outOfStock
                      ? 'opacity-50 cursor-not-allowed border-[var(--color-border)] bg-[var(--color-muted)]/30'
                      : inCart
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 shadow-sm'
                      : 'border-[var(--color-border)] bg-[var(--color-card)] hover:border-[var(--color-primary)]/50 hover:shadow-sm'
                  }`}
                >
                  {inCart && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--color-primary)] flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white">{inCart.qty}</span>
                    </div>
                  )}
                  <div className="w-8 h-8 rounded-md flex items-center justify-center mb-2"
                    style={{ backgroundColor: `${CATEGORY_COLORS[product.category] || 'var(--color-muted-foreground)'}18` }}>
                    <Icon name="Package" size={15} color={CATEGORY_COLORS[product.category] || 'var(--color-muted-foreground)'} />
                  </div>
                  <p className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)] leading-tight mb-1 line-clamp-2">{product.name}</p>
                  <p className="text-xs font-bold font-[var(--font-data)] text-[var(--color-primary)]">₹{Number(product.price).toFixed(2)}</p>
                  <p className={`text-[10px] font-[var(--font-caption)] mt-0.5 ${Number(product.stock_qty) <= product.low_stock_alert ? 'text-amber-500' : 'text-[var(--color-muted-foreground)]'}`}>
                    {outOfStock ? 'Out of stock' : `Stock: ${product.stock_qty}`}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Cart */}
      <div className="xl:w-80 flex-shrink-0">
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] flex flex-col">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <p className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Cart</p>
            {cart.length > 0 && (
              <button onClick={() => setCart([])} className="text-xs text-[var(--color-error)] hover:underline font-[var(--font-caption)]">Clear</button>
            )}
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto max-h-52 divide-y divide-[var(--color-border)]">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-1 text-[var(--color-muted-foreground)]">
                <Icon name="ShoppingCart" size={20} color="var(--color-muted-foreground)" />
                <p className="text-xs font-[var(--font-caption)]">Cart is empty</p>
              </div>
            ) : cart.map(item => (
              <div key={item.product_id} className="flex items-center gap-2 px-4 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)] truncate">{item.name}</p>
                  <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">₹{item.unit_price.toFixed(2)} each</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => updateQty(item.product_id, item.qty - 1)} className="w-5 h-5 rounded border border-[var(--color-border)] flex items-center justify-center hover:bg-[var(--color-muted)] text-xs">−</button>
                  <span className="w-6 text-center text-xs font-[var(--font-data)] text-[var(--color-foreground)]">{item.qty}</span>
                  <button
                    onClick={() => updateQty(item.product_id, item.qty + 1)}
                    disabled={item.qty >= item.stock_qty}
                    className="w-5 h-5 rounded border border-[var(--color-border)] flex items-center justify-center hover:bg-[var(--color-muted)] text-xs disabled:opacity-40"
                  >+</button>
                </div>
                <span className="text-xs font-bold font-[var(--font-data)] text-[var(--color-foreground)] w-14 text-right">₹{item.total.toFixed(2)}</span>
                <button onClick={() => removeFromCart(item.product_id)} className="text-[var(--color-error)] hover:opacity-70 ml-1">
                  <Icon name="X" size={12} color="var(--color-error)" />
                </button>
              </div>
            ))}
          </div>

          <div className="px-4 py-3 border-t border-[var(--color-border)] space-y-3">
            {/* Member search */}
            <div className="relative">
              <input
                value={selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : memberSearch}
                onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                placeholder="Link member (optional)"
                className="w-full px-3 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
              />
              {selectedMember && (
                <button onClick={() => { setSelectedMember(null); setMemberSearch(''); }} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Icon name="X" size={10} color="var(--color-muted-foreground)" />
                </button>
              )}
              {memberResults.length > 0 && !selectedMember && (
                <div className="absolute top-full left-0 right-0 z-10 bg-[var(--color-card)] border border-[var(--color-border)] rounded-md shadow-lg mt-1 overflow-hidden">
                  {memberResults.map(m => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedMember(m); setMemberSearch(''); setMemberResults([]); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--color-muted)] text-[var(--color-foreground)] font-[var(--font-caption)]"
                    >
                      {m.first_name} {m.last_name}
                      {m.phone && <span className="text-[var(--color-muted-foreground)] ml-1">· {m.phone}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Discount + Tax */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-0.5">Discount (₹)</label>
                <input
                  type="number" min="0" value={discount} onChange={e => setDiscount(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                  placeholder="0"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-0.5">Tax (₹)</label>
                <input
                  type="number" min="0" value={tax} onChange={e => setTax(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Payment method */}
            <div>
              <label className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-0.5">Payment Method</label>
              <div className="flex flex-wrap gap-1">
                {PAYMENT_METHODS.map(pm => (
                  <button
                    key={pm.value}
                    onClick={() => setPaymentMethod(pm.value)}
                    className={`px-2 py-1 text-[10px] font-medium font-[var(--font-caption)] rounded border transition-colors ${
                      paymentMethod === pm.value
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/40'
                    }`}
                  >{pm.label}</button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={1}
              className="w-full px-2 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] resize-none"
            />

            {/* Total */}
            <div className="flex items-center justify-between py-1 border-t border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Subtotal</span>
              <span className="text-xs font-[var(--font-data)] text-[var(--color-foreground)]">₹{subtotal.toFixed(2)}</span>
            </div>
            {discountAmt > 0 && (
              <div className="flex items-center justify-between -mt-2">
                <span className="text-xs text-green-500 font-[var(--font-caption)]">Discount</span>
                <span className="text-xs font-[var(--font-data)] text-green-500">−₹{discountAmt.toFixed(2)}</span>
              </div>
            )}
            {taxAmt > 0 && (
              <div className="flex items-center justify-between -mt-2">
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Tax</span>
                <span className="text-xs font-[var(--font-data)] text-[var(--color-foreground)]">+₹{taxAmt.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Total</span>
              <span className="text-sm font-bold font-[var(--font-data)] text-[var(--color-primary)]">₹{total.toFixed(2)}</span>
            </div>

            <button
              onClick={completeSale}
              disabled={completing || cart.length === 0 || !branchId}
              className="w-full py-2.5 text-sm font-semibold font-[var(--font-caption)] bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {completing ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</>
              ) : (
                <><Icon name="ShoppingBag" size={15} color="white" />Complete Sale</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-tab: Products Management ────────────────────────────────────────────

function ProductsTab({ branchId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'other', sku: '', price: '', cost_price: '', stock_qty: 0, low_stock_alert: 5, image_url: '' });
  const [stockModal, setStockModal] = useState(null);
  const [stockAdjust, setStockAdjust] = useState('');

  const fetchProducts = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/products', {
        params: { branch_id: branchId, search: search || undefined, include_inactive: true },
      });
      setProducts(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [branchId, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', category: 'other', sku: '', price: '', cost_price: '', stock_qty: 0, low_stock_alert: 5, image_url: '' });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      sku: p.sku || '',
      price: p.price,
      cost_price: p.cost_price || '',
      stock_qty: p.stock_qty,
      low_stock_alert: p.low_stock_alert,
      image_url: p.image_url || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name || !form.price) return alert('Name and price are required');
    setSaving(true);
    try {
      const payload = { ...form, branch_id: branchId, price: Number(form.price), stock_qty: Number(form.stock_qty), low_stock_alert: Number(form.low_stock_alert), cost_price: form.cost_price !== '' ? Number(form.cost_price) : undefined };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
      } else {
        await api.post('/products', payload);
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (product) => {
    try {
      await api.put(`/products/${product.id}`, { is_active: !product.is_active });
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  const adjustStock = async () => {
    if (!stockAdjust) return;
    try {
      await api.post(`/products/${stockModal.id}/stock`, { adjustment: Number(stockAdjust) });
      setStockModal(null);
      setStockAdjust('');
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to adjust stock');
    }
  };

  const lowStockCount = products.filter(p => p.is_active && Number(p.stock_qty) <= p.low_stock_alert).length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex-1 min-w-[160px] relative">
          <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
          />
        </div>
        {lowStockCount > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-500 text-xs font-medium font-[var(--font-caption)]">
            <Icon name="AlertTriangle" size={13} color="#f59e0b" />
            {lowStockCount} low stock
          </div>
        )}
        <button
          onClick={openAdd}
          disabled={!branchId}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Icon name="Plus" size={14} color="white" />
          Add Product
        </button>
      </div>

      {!branchId ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-12 h-12 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Icon name="MapPin" size={22} color="var(--color-primary)" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">No branch selected</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Select a branch from the top navigation to manage products</p>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-[var(--color-muted-foreground)]">
          <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
          <p className="text-sm font-[var(--font-caption)]">No products yet. Add your first product.</p>
        </div>
      ) : (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Product</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] hidden sm:table-cell">Category</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Price</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Stock</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {products.map(p => {
                const isLow = p.is_active && Number(p.stock_qty) <= p.low_stock_alert;
                return (
                  <tr key={p.id} className={`hover:bg-[var(--color-muted)]/20 ${!p.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">{p.name}</p>
                        {p.sku && <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-data)]">SKU: {p.sku}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium font-[var(--font-caption)]"
                        style={{ backgroundColor: `${CATEGORY_COLORS[p.category]}18`, color: CATEGORY_COLORS[p.category] }}>
                        {CATEGORIES.find(c => c.value === p.category)?.label || p.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)]">₹{Number(p.price).toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold font-[var(--font-data)] ${isLow ? 'text-amber-500' : 'text-[var(--color-foreground)]'}`}>
                        {p.stock_qty}
                        {isLow && <Icon name="AlertTriangle" size={11} color="#f59e0b" className="inline ml-1" />}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setStockModal(p); setStockAdjust(''); }}
                          className="p-1.5 rounded text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                          title="Adjust stock"
                        >
                          <Icon name="Package" size={13} color="currentColor" />
                        </button>
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 rounded text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                        >
                          <Icon name="Pencil" size={13} color="currentColor" />
                        </button>
                        <button
                          onClick={() => toggleActive(p)}
                          className={`p-1.5 rounded transition-colors ${p.is_active ? 'text-green-500 hover:bg-green-500/10' : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]'}`}
                          title={p.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <Icon name={p.is_active ? 'ToggleRight' : 'ToggleLeft'} size={15} color="currentColor" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !saving && setShowModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{editing ? 'Edit Product' : 'Add Product'}</h3>
              <button onClick={() => !saving && setShowModal(false)}><Icon name="X" size={18} color="var(--color-muted-foreground)" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                  placeholder="Product name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">SKU</label>
                  <input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Selling Price (₹) *</label>
                  <input type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Cost Price (₹)</label>
                  <input type="number" min="0" value={form.cost_price} onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Stock Qty</label>
                  <input type="number" min="0" value={form.stock_qty} onChange={e => setForm(f => ({ ...f, stock_qty: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="0" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Low Stock Alert</label>
                  <input type="number" min="0" value={form.low_stock_alert} onChange={e => setForm(f => ({ ...f, low_stock_alert: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="5" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => !saving && setShowModal(false)}
                className="flex-1 py-2 text-sm border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-lg hover:bg-[var(--color-muted)] transition-colors font-[var(--font-caption)]">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                className="flex-1 py-2 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] disabled:opacity-50 transition-colors font-[var(--font-caption)] flex items-center justify-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                {editing ? 'Save Changes' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjust Modal */}
      {stockModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setStockModal(null)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-xs p-5">
            <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-1">Adjust Stock</h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-4">{stockModal.name} · Current: {stockModal.stock_qty}</p>
            <label className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] block mb-1">Adjustment (+ to add, − to remove)</label>
            <input
              type="number"
              value={stockAdjust}
              onChange={e => setStockAdjust(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)] mb-4"
              placeholder="e.g. 10 or -5"
            />
            <div className="flex gap-2">
              <button onClick={() => setStockModal(null)} className="flex-1 py-2 text-sm border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-lg hover:bg-[var(--color-muted)] transition-colors font-[var(--font-caption)]">Cancel</button>
              <button onClick={adjustStock} className="flex-1 py-2 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] transition-colors font-[var(--font-caption)]">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-tab: Sales Report ────────────────────────────────────────────────────

function SalesTab({ branches }) {
  const [selectedBranch, setSelectedBranch] = useState('');
  const [sales, setSales] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [period, setPeriod] = useState('month');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBranch) params.branch_id = selectedBranch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [salesRes, reportRes] = await Promise.all([
        api.get('/pos-sales', { params: { ...params, limit: 50 } }),
        api.get('/pos-sales/report', { params: { ...params, period } }),
      ]);
      setSales(salesRes.data || []);
      setReport(reportRes.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, dateFrom, dateTo, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={selectedBranch}
          onChange={e => setSelectedBranch(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
        >
          <option value="">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]" />
        <select value={period} onChange={e => setPeriod(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]">
          <option value="week">Top Products: This Week</option>
          <option value="month">Top Products: This Month</option>
          <option value="all">Top Products: All Time</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary KPIs */}
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Today's Sales", value: report.today.count, sub: `₹${Number(report.today.revenue).toFixed(2)}`, color: 'var(--color-primary)', icon: 'ShoppingBag' },
                { label: "This Month", value: report.this_month.count, sub: `₹${Number(report.this_month.revenue).toFixed(2)}`, color: 'var(--color-accent)', icon: 'TrendingUp' },
                { label: "Top Category", value: report.revenue_by_category[0]?.category || '—', sub: report.revenue_by_category[0] ? `₹${Number(report.revenue_by_category[0].revenue).toFixed(2)}` : '', color: '#22c55e', icon: 'Tag' },
                { label: "Top Product", value: report.top_products[0]?.name || '—', sub: report.top_products[0] ? `${report.top_products[0].total_qty} units` : '', color: 'var(--color-warning)', icon: 'Package' },
              ].map((kpi, i) => (
                <div key={i} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi.color}18` }}>
                    <Icon name={kpi.icon} size={15} color={kpi.color} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{kpi.label}</p>
                    <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] truncate">{kpi.value}</p>
                    {kpi.sub && <p className="text-xs font-[var(--font-data)]" style={{ color: kpi.color }}>{kpi.sub}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Top Products */}
            {report && report.top_products.length > 0 && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
                <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-3">Top Products</p>
                <div className="space-y-2">
                  {report.top_products.slice(0, 8).map((p, i) => (
                    <div key={p.product_id} className="flex items-center gap-2">
                      <span className="w-5 text-[10px] font-bold font-[var(--font-data)] text-[var(--color-muted-foreground)]">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)] truncate">{p.name}</p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{p.total_qty} units</p>
                      </div>
                      <span className="text-xs font-bold font-[var(--font-data)] text-[var(--color-primary)]">₹{Number(p.total_revenue).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revenue by Category */}
            {report && report.revenue_by_category.length > 0 && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
                <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-3">Revenue by Category</p>
                <div className="space-y-3">
                  {report.revenue_by_category.map(c => {
                    const total = report.revenue_by_category.reduce((s, x) => s + Number(x.revenue), 0);
                    const pct = total > 0 ? (Number(c.revenue) / total * 100) : 0;
                    const color = CATEGORY_COLORS[c.category] || 'var(--color-muted-foreground)';
                    return (
                      <div key={c.category}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] capitalize">{c.category}</span>
                          <span className="text-xs font-bold font-[var(--font-data)] text-[var(--color-foreground)]">₹{Number(c.revenue).toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 bg-[var(--color-muted)] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Sales */}
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
              <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-3">Recent Sales</p>
              {sales.length === 0 ? (
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No sales found</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {sales.map(s => (
                    <div key={s.id} className="flex items-start justify-between gap-2 py-1.5 border-b border-[var(--color-border)] last:border-0">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold font-[var(--font-data)] text-[var(--color-primary)]">{s.sale_number}</p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                          {s.branch_name}{s.member_name ? ` · ${s.member_name}` : ''}
                        </p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                          {new Date(s.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold font-[var(--font-data)] text-[var(--color-foreground)]">₹{Number(s.total).toFixed(2)}</p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] capitalize">{s.payment_method}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main POSManager ──────────────────────────────────────────────────────────

const POS_SUB_TABS = [
  { key: 'terminal', label: 'POS Terminal', icon: 'Monitor' },
  { key: 'products', label: 'Products', icon: 'Package' },
  { key: 'sales', label: 'Sales & Reports', icon: 'BarChart2' },
];

export default function POSManager() {
  const [subTab, setSubTab] = useState('terminal');
  const { branches, selectedBranchId } = useBranch();

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
        {POS_SUB_TABS.map(st => (
          <button
            key={st.key}
            onClick={() => setSubTab(st.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap font-[var(--font-caption)] ${
              subTab === st.key
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
            }`}
          >
            <Icon name={st.icon} size={14} color={subTab === st.key ? 'var(--color-primary)' : 'currentColor'} />
            {st.label}
          </button>
        ))}
      </div>

      {subTab === 'terminal' && <POSTerminal branchId={selectedBranchId} />}
      {subTab === 'products' && <ProductsTab branchId={selectedBranchId} />}
      {subTab === 'sales' && <SalesTab branches={branches} />}
    </div>
  );
}
